import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAmbassador } from '../context/AmbassadorContext';
import { NofeeNavLogo } from '../components/NoFeeLogo';
import { GoldBellMark } from '../components/NoFeeLogo';
import {
  Copy,
  Check,
  Share2,
  LogOut,
  Trophy,
  DollarSign,
  Hourglass,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import AmbassadorShareCard from '../components/AmbassadorShareCard';

const statusBadge = {
  pending_verification: { icon: Hourglass, label: 'Pending verification', cls: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30' },
  verified: { icon: Clock, label: 'Verified — releasing soon', cls: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  reward_released: { icon: CheckCircle2, label: 'Reward released', cls: 'bg-green-500/10 text-green-300 border-green-500/30' },
  rejected: { icon: XCircle, label: 'Rejected', cls: 'bg-red-500/10 text-red-300 border-red-500/30' },
};

const AmbassadorDashboard = () => {
  const { ambassador, stats, token, logout, api, fetchMe } = useAmbassador();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shareToast, setShareToast] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/rewards/login');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [refRes, ledgerRes] = await Promise.all([
        api.get('/rewards/ambassadors/me/referrals'),
        api.get('/rewards/ambassadors/me/rewards'),
      ]);
      setReferrals(refRes.data);
      setLedger(ledgerRes.data);
      await fetchMe();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!ambassador) {
    return <div className="min-h-screen flex items-center justify-center bg-black text-white"><div className="animate-spin w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full" /></div>;
  }

  const shareUrl = `${window.location.origin}/register?ref=${ambassador.referral_code}`;
  const ambassadorLink = `${window.location.origin}/rewards/signup?ref=${ambassador.referral_code}`;

  const copyCode = () => {
    navigator.clipboard.writeText(ambassador.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sharePlatform = async (platform) => {
    // Award rewards points via backend for analytics + tier progression
    try {
      const res = await api.post('/rewards/share', { platform });
      if (res?.data?.points_awarded > 0) {
        setShareToast(`+${res.data.points_awarded} points!`);
        setTimeout(() => setShareToast(''), 2500);
        await fetchMe();
      }
    } catch {
      /* silent — share UX must not be blocked by analytics */
    }
  };

  const totalPending = referrals.filter((r) => r.status === 'pending_verification' || r.status === 'verified').length;
  const pendingValue = referrals
    .filter((r) => r.status === 'pending_verification' || r.status === 'verified')
    .reduce((s, r) => s + (r.reward_amount || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="border-b border-gold-900/30 sticky top-0 z-40 bg-black/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/rewards"><NofeeNavLogo height={32} /></Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30">
              <Trophy className="w-4 h-4 text-gold-400" />
              <span className="text-sm font-bold text-gold-300">{ambassador.tier}</span>
            </div>
            <button
              onClick={() => { logout(); navigate('/rewards'); }}
              data-testid="amb-logout"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/60 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Greeting + code */}
        <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center mb-10">
          <div>
            <p className="text-gold-400 text-sm uppercase tracking-wider font-bold mb-2">Welcome back</p>
            <h1 className="text-4xl sm:text-5xl font-black">Hi, {ambassador.name.split(' ')[0]}.</h1>
            <p className="text-white/60 mt-2">Keep asking restaurants <span className="text-gold-300 italic">"Do you accept NoFee?"</span> — each one you bring in = $20.</p>
          </div>
          <div className="justify-self-center inline-flex items-center justify-center">
            <GoldBellMark size={88} />
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Credit balance" value={`$${ambassador.credit_balance.toFixed(2)}`} icon={DollarSign} accent />
          <StatCard label="Total earned" value={`$${ambassador.total_earned.toFixed(2)}`} icon={CheckCircle2} />
          <StatCard label="Pending" value={`${totalPending} ($${pendingValue.toFixed(2)})`} icon={Hourglass} />
          <StatCard label="Points" value={`${ambassador.points} pts`} icon={Trophy} />
        </div>

        {/* Referral code card */}
        <div className="relative p-8 rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/10 via-black to-black mb-10 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-64 h-64 bg-gold-500/20 rounded-full blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold-400 mb-2">Your referral code</p>
              <div className="flex items-center gap-3">
                <code data-testid="amb-referral-code" className="text-3xl sm:text-4xl font-black tracking-tight bg-gradient-to-b from-gold-200 via-gold-400 to-gold-600 bg-clip-text text-transparent">
                  {ambassador.referral_code}
                </code>
                <button
                  onClick={copyCode}
                  data-testid="amb-copy-code"
                  className="p-2 rounded-lg bg-white/5 hover:bg-gold-500/20 border border-gold-500/30 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gold-400" />}
                </button>
              </div>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">
                Restaurants enter this code when signing up. After they're verified, you earn <span className="text-gold-400 font-semibold">$20</span> 20 days later.
              </p>
            </div>

            <div>
              <AmbassadorShareCard
                referralCode={ambassador.referral_code}
                shareUrl={shareUrl}
                onShare={sharePlatform}
              />
              {shareToast && (
                <div
                  className="mt-3 text-xs text-gold-400 font-semibold animate-pulse"
                  data-testid="amb-share-toast"
                >
                  {shareToast}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Referrals list */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 p-6 rounded-3xl border border-gold-500/20 bg-white/[0.02]">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">My referrals</h2>
              <button onClick={loadData} className="text-xs text-white/40 hover:text-gold-400">Refresh</button>
            </div>
            {loading ? (
              <div className="text-white/40 text-sm" data-testid="amb-referrals-list">Loading…</div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-12 text-white/50 text-sm" data-testid="amb-referrals-list">
                No referrals yet. Share your code <code className="text-gold-400 font-bold">{ambassador.referral_code}</code> with a restaurant!
              </div>
            ) : (
              <ul className="space-y-3" data-testid="amb-referrals-list">
                {referrals.map((r) => {
                  const meta = statusBadge[r.status] || statusBadge.pending_verification;
                  const Icon = meta.icon;
                  return (
                    <li key={r.id} className="p-4 rounded-xl bg-black/40 border border-gold-500/10 hover:border-gold-500/30 transition-colors">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="font-semibold">{r.restaurant_name}</div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-white/50">
                        <span>${r.reward_amount.toFixed(2)} reward</span>
                        {r.days_until_release !== null && r.status === 'verified' && (
                          <span className="text-blue-300 font-semibold">{r.days_until_release} days left</span>
                        )}
                        {r.status === 'reward_released' && (
                          <span className="text-green-400 font-semibold">+${r.reward_amount.toFixed(2)} credited</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Credit ledger */}
          <div className="p-6 rounded-3xl border border-gold-500/20 bg-white/[0.02]">
            <h2 className="text-xl font-bold mb-5">Earnings</h2>
            {ledger.length === 0 ? (
              <div className="text-white/40 text-sm" data-testid="amb-ledger-list">No earnings yet.</div>
            ) : (
              <ul className="space-y-2" data-testid="amb-ledger-list">
                {ledger.map((e) => (
                  <li key={e.id} className="flex items-center justify-between py-2 border-b border-gold-900/20 last:border-0 text-sm">
                    <div>
                      <div className="font-semibold">+${e.amount.toFixed(2)}</div>
                      <div className="text-xs text-white/40">{e.restaurant_name || e.source}</div>
                    </div>
                    <div className="text-xs text-white/40">{new Date(e.created_at).toLocaleDateString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Invite another ambassador */}
        <div className="mt-10 p-6 rounded-3xl border border-gold-500/20 bg-gradient-to-br from-white/[0.02] to-transparent text-sm text-white/60">
          Want to grow your tier faster? Invite friends to become NoFee Ambassadors too — they sign up with your link, you earn{' '}
          <span className="text-gold-400 font-bold">+25 points</span> per new ambassador:
          <div className="mt-2 flex items-center gap-2 text-xs">
            <code className="px-2 py-1 rounded bg-black/60 border border-gold-500/20 text-gold-300">{ambassadorLink}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(ambassadorLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="text-gold-400 font-bold hover:text-gold-300"
            >
              Copy
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className={`p-5 rounded-2xl border ${accent ? 'border-gold-500/40 bg-gold-500/[0.06]' : 'border-gold-500/15 bg-white/[0.02]'}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${accent ? 'text-gold-400' : 'text-white/40'}`} />
      <span className="text-[11px] uppercase tracking-wider font-bold text-white/50">{label}</span>
    </div>
    <div className={`text-2xl font-black ${accent ? 'text-gold-400' : 'text-white'}`}>{value}</div>
  </div>
);

export default AmbassadorDashboard;
